import { useState } from "react";
import axios from "axios";
import { Lock, MessageCircle } from "lucide-react";
import PageHead from "../components/PageHead";
import ParticleField from "../components/ParticleField";
import Reveal from "../components/Reveal";
import { heroBgStyle } from "../lib/heroBg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Software Development",
  "Website Design",
  "Video Production",
  "Content Creation",
  "Media Consulting",
  "Printing & Publishing",
  "Other",
];
const BUDGETS = ["Under ₦100k", "₦100k – ₦500k", "₦500k – ₦1M", "Above ₦1M", "Prefer not to say"];
const TIMELINES = ["ASAP", "Within 1 Month", "1–3 Months", "3–6 Months", "Flexible"];
const REFERRAL = ["Google", "Social Media", "Referral", "Word of Mouth", "Other"];

const initial = {
  full_name: "",
  email: "",
  phone: "",
  organization: "",
  service_needed: "",
  project_description: "",
  budget_range: "",
  timeline: "",
  referral_source: "",
};

function validate(v) {
  const e = {};
  if (!v.full_name.trim() || v.full_name.trim().length < 2) e.full_name = "Please enter your full name.";
  if (!/^\S+@\S+\.\S+$/.test(v.email)) e.email = "Enter a valid email address.";
  if (!v.phone.trim() || v.phone.trim().length < 5) e.phone = "Enter a valid phone number.";
  if (!v.service_needed) e.service_needed = "Select a service.";
  if (!v.project_description.trim() || v.project_description.trim().length < 10)
    e.project_description = "Please describe your project (at least 10 characters).";
  if (!v.budget_range) e.budget_range = "Select a budget range.";
  if (!v.timeline) e.timeline = "Select a timeline.";
  if (!v.referral_source) e.referral_source = "Tell us how you heard about us.";
  return e;
}

const Field = ({ children, error, testId }) => (
  <div className={`float-field ${error ? "field-error" : ""}`} data-testid={testId}>
    {children}
    {error ? <p className="error-text">{error}</p> : null}
  </div>
);

export default function RequestQuote() {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const onChange = (e) => {
    setValues((v) => ({ ...v, [e.target.name]: e.target.value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(values);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus({ state: "submitting", msg: "" });
    try {
      await axios.post(`${API}/quote`, values);
      setStatus({
        state: "success",
        msg: "Quote request received! We'll be in touch within 24 hours with a tailored proposal.",
      });
      setValues(initial);
    } catch (err) {
      console.error(err);
      setStatus({
        state: "error",
        msg: "Something went wrong submitting your request. Please try again or message us on WhatsApp.",
      });
    }
  };

  return (
    <main data-testid="quote-page">
      <PageHead
        title="Request a Free Quote | Digital Archdeacon Media Ltd"
        description="Tell us about your project — we'll respond within 24 hours with a tailored proposal."
      />

      <section className="relative pt-36 pb-10 overflow-hidden" style={heroBgStyle} data-testid="quote-hero">
        <ParticleField density={45} />
        <div className="absolute inset-0 bg-grid opacity-25 pointer-events-none" />
        <div className="container-x relative">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">Request a free quote</span>
            <h1 className="h-display text-[clamp(3rem,8vw,7rem)] mt-3">
              Tell us about your <span className="text-gradient-blue">project</span>.
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-[#CBD5E1]">
              Tell us about your project and we&apos;ll get back to you within 24 hours with a tailored proposal.
            </p>
          </Reveal>
        </div>
      </section>

      <div className="bg-navy-gradient">
        <section className="pb-12" data-testid="quote-main">
          <div className="container-x pt-12">
            <Reveal>
              <form
                onSubmit={onSubmit}
                noValidate
                className="glass p-6 sm:p-8 md:p-12 max-w-3xl mx-auto"
                data-testid="quote-form"
              >
                <div className="grid gap-5 md:grid-cols-2">
                  <Field error={errors.full_name} testId="quote-field-full-name">
                    <input id="q-full_name" name="full_name" type="text" value={values.full_name} onChange={onChange} placeholder=" " autoComplete="off" data-testid="quote-full-name" />
                    <label htmlFor="q-full_name">Full Name *</label>
                  </Field>
                  <Field error={errors.email} testId="quote-field-email">
                    <input id="q-email" name="email" type="email" value={values.email} onChange={onChange} placeholder=" " autoComplete="off" data-testid="quote-email" />
                    <label htmlFor="q-email">Email Address *</label>
                  </Field>
                  <Field error={errors.phone} testId="quote-field-phone">
                    <input id="q-phone" name="phone" type="tel" value={values.phone} onChange={onChange} placeholder=" " autoComplete="off" data-testid="quote-phone" />
                    <label htmlFor="q-phone">Phone Number *</label>
                  </Field>
                  <Field error={errors.organization} testId="quote-field-organization">
                    <input id="q-organization" name="organization" type="text" value={values.organization} onChange={onChange} placeholder=" " autoComplete="off" data-testid="quote-organization" />
                    <label htmlFor="q-organization">Organization / Company Name</label>
                  </Field>

                  <Field error={errors.service_needed} testId="quote-field-service">
                    <select id="q-service_needed" name="service_needed" value={values.service_needed} onChange={onChange} className={values.service_needed ? "has-value" : ""} data-testid="quote-service">
                      <option value="" disabled hidden></option>
                      {SERVICES.map((o) => (<option key={o} value={o}>{o}</option>))}
                    </select>
                    <label htmlFor="q-service_needed">Service Needed *</label>
                  </Field>

                  <Field error={errors.budget_range} testId="quote-field-budget">
                    <select id="q-budget_range" name="budget_range" value={values.budget_range} onChange={onChange} className={values.budget_range ? "has-value" : ""} data-testid="quote-budget">
                      <option value="" disabled hidden></option>
                      {BUDGETS.map((o) => (<option key={o} value={o}>{o}</option>))}
                    </select>
                    <label htmlFor="q-budget_range">Budget Range *</label>
                  </Field>

                  <Field error={errors.timeline} testId="quote-field-timeline">
                    <select id="q-timeline" name="timeline" value={values.timeline} onChange={onChange} className={values.timeline ? "has-value" : ""} data-testid="quote-timeline">
                      <option value="" disabled hidden></option>
                      {TIMELINES.map((o) => (<option key={o} value={o}>{o}</option>))}
                    </select>
                    <label htmlFor="q-timeline">Timeline *</label>
                  </Field>

                  <Field error={errors.referral_source} testId="quote-field-referral">
                    <select id="q-referral_source" name="referral_source" value={values.referral_source} onChange={onChange} className={values.referral_source ? "has-value" : ""} data-testid="quote-referral">
                      <option value="" disabled hidden></option>
                      {REFERRAL.map((o) => (<option key={o} value={o}>{o}</option>))}
                    </select>
                    <label htmlFor="q-referral_source">How did you hear about us? *</label>
                  </Field>
                </div>

                <div className="mt-5">
                  <Field error={errors.project_description} testId="quote-field-description">
                    <textarea id="q-project_description" name="project_description" value={values.project_description} onChange={onChange} placeholder=" " rows={6} data-testid="quote-description" />
                    <label htmlFor="q-project_description">Project Description *</label>
                  </Field>
                </div>

                <button
                  type="submit"
                  disabled={status.state === "submitting"}
                  data-testid="quote-submit"
                  className="glow-btn mt-8 w-full inline-flex items-center justify-center rounded-full bg-electric px-8 py-5 text-base font-semibold text-white disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status.state === "submitting" ? "Submitting…" : "Submit My Request"}
                </button>

                {status.state === "success" ? (
                  <p data-testid="quote-success" className="mt-6 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-200 px-4 py-3 text-sm">{status.msg}</p>
                ) : null}
                {status.state === "error" ? (
                  <p data-testid="quote-failure" className="mt-6 rounded-xl border border-red-500/30 bg-red-500/10 text-red-200 px-4 py-3 text-sm">{status.msg}</p>
                ) : null}

                <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5 pt-6 border-t border-white/10">
                  <p className="inline-flex items-center gap-2 text-sm text-[#CBD5E1]">
                    <Lock className="h-4 w-4 text-electric" />
                    Your information is 100% confidential. We&apos;ll respond within 24 hours.
                  </p>
                  <a
                    href="https://wa.me/2348147276648"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="quote-whatsapp"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#25D366] hover:text-emerald-300 transition-colors"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Prefer to chat directly? Message us on WhatsApp
                  </a>
                </div>
              </form>
            </Reveal>
          </div>
        </section>

        {/* Unique CTA */}
        <section className="section-pad pt-0" data-testid="quote-cta">
          <div className="container-x">
            <Reveal>
              <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-cta-gradient p-10 md:p-14 text-center">
                <div className="absolute -top-24 -right-12 h-72 w-72 rounded-full bg-electric/40 blur-3xl" />
                <span className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">What happens next</span>
                <h2 className="h-display text-4xl md:text-5xl mt-3">
                  You&apos;ll hear back from a real human in 24 hours.
                </h2>
                <p className="mt-5 max-w-2xl mx-auto text-[#CBD5E1] text-base sm:text-lg">
                  We read every brief carefully, ask any clarifying questions by email, and send a tailored proposal — never a templated one.
                </p>
              </div>
            </Reveal>
          </div>
        </section>
      </div>
    </main>
  );
}
